import { useState } from 'react';
import { departmentHcNonhcRows, shortDeptLabel } from './globalReporting';
import { GlobalRecordsModal } from './GlobalDrillModal';
import { MetricCells, MetricHeaders } from './GlobalRecordDetails';

// Same layout and drill-down as every department's own HC & Non-HC 360 tab
// (GAHCNonHC360.js) — Headcount rows, an "HC Cost Total" row, Non-Headcount
// rows, a "Non-HC Total" row, then a grand Total row — just with
// "department" standing in for "category" as the row dimension, since
// Global spans 9 departments each with their own unrelated category
// taxonomy (same substitution the Summary tab's "By Department" table
// already makes). Each department row opens that department's own records
// (GlobalRecordsModal, same component every department dashboard's own HC &
// Non-HC 360 tab uses under the hood) — Global has no combined record model
// of its own, but once a single department is picked, its own HC/Non-HC
// records are fetchable just like any department dashboard's. The 3 Total
// rows only open records once the page-level Department filter has already
// narrowed the view to one department — a "Total" spanning multiple
// departments' different record schemas has no single list to show.
function GlobalHcNonHc({ data, department = [], costCategory = [] }) {
    const currentYear = data.current_year;
    const priorYear = data.prior_year;
    const allMonths = (data.global_total.total.monthly || []).map(m => m.month);
    const byCategory = data.global_total.by_category;
    const { hcRows, nonhcRows, commissionRows } = departmentHcNonhcRows(byCategory, department);
    // Category (Headcount/Non-Headcount/Commission) is multi-select too — an
    // empty selection means show every section, same convention as every
    // other Global tab's Category filter. Same Category filter as every
    // department dashboard's own HC & Non-HC 360 tab (GAHCNonHC360.js) —
    // excluding Headcount collapses out the Non-Headcount section entirely
    // (and vice versa) instead of just zeroing it, since this tab IS the
    // HC/Non-HC split, so excluding one means don't show that section at all.
    const showHc = !costCategory.length || costCategory.includes('hc');
    const showNonhc = !costCategory.length || costCategory.includes('nonhc');
    const showCommission = !costCategory.length || costCategory.includes('commission');
    const sumRows = rows => rows.reduce((s, r) => ({ actual: s.actual + r.actual, aop: s.aop + r.aop, py: s.py + r.py }), { actual: 0, aop: 0, py: 0 });
    const hcTotal = showHc ? sumRows(hcRows) : { actual: 0, aop: 0, py: 0 };
    const nonhcTotal = showNonhc ? sumRows(nonhcRows) : { actual: 0, aop: 0, py: 0 };
    const commissionTotal = showCommission ? sumRows(commissionRows) : { actual: 0, aop: 0, py: 0 };
    const grandTotal = {
        actual: hcTotal.actual + nonhcTotal.actual + commissionTotal.actual,
        aop: hcTotal.aop + nonhcTotal.aop + commissionTotal.aop,
        py: hcTotal.py + nonhcTotal.py + commissionTotal.py,
    };

    const [drill, setDrill] = useState(null);
    const openDrill = (source, dept, label) => setDrill({ source, department: dept, title: `${shortDeptLabel(dept)} — ${label}` });
    // Only meaningful once the page filter has already narrowed to exactly
    // one department — a "Total" spanning multiple departments' different
    // record schemas has no single list to show.
    const singleDepartment = department.length === 1 ? department[0] : null;
    const canViewTotals = !!singleDepartment;

    return <div className="esd">
        {drill && (
            <GlobalRecordsModal
                title={drill.title} sub={`FY${currentYear?.slice(2)}`} department={drill.department} source={drill.source}
                category={null} months={allMonths} currentYear={currentYear} priorYear={priorYear}
                onClose={() => setDrill(null)}
            />
        )}
        <div className="esd-section"><div className="esd-section-head"><span className="esd-section-title">HC &amp; Non-HC 360 — FY{currentYear?.slice(2)}{department.length ? ` — ${department.map(shortDeptLabel).join(' + ')}` : ''}</span></div>
            <div className="esd-table-wrap"><table className="esd-table"><thead><tr><th>Cost / Department</th><MetricHeaders /></tr></thead>
                <tbody>
                    {showHc && hcRows.map(row => <tr key={`hc-${row.department}`} className="esd-row-cat esd-row-cat--hc" onClick={() => openDrill('hc', row.department, 'Headcount')}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('hc', row.department, 'Headcount'); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {showHc && !!hcRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--hc"
                            onClick={canViewTotals ? () => openDrill('hc', singleDepartment, 'Headcount') : undefined}
                            style={{ cursor: canViewTotals ? 'pointer' : 'default' }}
                        >
                            <td>{canViewTotals ? <button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('hc', singleDepartment, 'Headcount'); }}>HC Cost Total — view details</button> : 'HC Cost Total'}</td>
                            <MetricCells row={hcTotal} />
                        </tr>
                    )}
                    {showNonhc && nonhcRows.map(row => <tr key={`nonhc-${row.department}`} className="esd-row-cat esd-row-cat--nonhc" onClick={() => openDrill('nonhc', row.department, 'Non-Headcount')}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('nonhc', row.department, 'Non-Headcount'); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {showNonhc && !!nonhcRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--nonhc"
                            onClick={canViewTotals ? () => openDrill('nonhc', singleDepartment, 'Non-Headcount') : undefined}
                            style={{ cursor: canViewTotals ? 'pointer' : 'default' }}
                        >
                            <td>{canViewTotals ? <button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('nonhc', singleDepartment, 'Non-Headcount'); }}>Non-HC Total — view details</button> : 'Non-HC Total'}</td>
                            <MetricCells row={nonhcTotal} />
                        </tr>
                    )}
                    {showCommission && commissionRows.map(row => <tr key={`commission-${row.department}`} className="esd-row-cat esd-row-cat--commission" onClick={() => openDrill('commission', row.department, 'Commission')}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('commission', row.department, 'Commission'); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {showCommission && !!commissionRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--commission"
                            onClick={canViewTotals ? () => openDrill('commission', singleDepartment, 'Commission') : undefined}
                            style={{ cursor: canViewTotals ? 'pointer' : 'default' }}
                        >
                            <td>{canViewTotals ? <button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('commission', singleDepartment, 'Commission'); }}>Commission Total — view details</button> : 'Commission Total'}</td>
                            <MetricCells row={commissionTotal} />
                        </tr>
                    )}
                    {(!showHc || !hcRows.length) && (!showNonhc || !nonhcRows.length) && (!showCommission || !commissionRows.length) && (
                        <tr><td colSpan={6} className="esd-empty-row">No department data found.</td></tr>
                    )}
                </tbody>
                <tfoot>
                    <tr
                        className="esd-row-total esd-row-total--grand"
                        onClick={canViewTotals ? () => openDrill('total', singleDepartment, 'Total') : undefined}
                        style={{ cursor: canViewTotals ? 'pointer' : 'default' }}
                    >
                        <td>{canViewTotals ? <button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('total', singleDepartment, 'Total'); }}>Total — view details</button> : 'Total'}</td>
                        <MetricCells row={grandTotal} />
                    </tr>
                </tfoot>
            </table></div>
        </div>
    </div>;
}
export default GlobalHcNonHc;
export { GlobalHcNonHc };
